import type { ReactNode } from "react";
import type React from "react";
import { createElement, Fragment } from "react";

interface ProcessHtmlOptions {
  /**
   * Function to handle navigation for links
   */
  navigate?: (path: string) => void;
  /**
   * CSS class for the outer container
   */
  className?: string;
  /**
   * Component to wrap paragraphs in (e.g., Typography)
   */
  ParagraphComponent?: React.ComponentType<any>;
  /**
   * Whether to split spans into separate paragraphs
   */
  splitSpansIntoParagraphs?: boolean;
}

/**
 * Processes HTML content to handle links and optionally split spans into paragraphs
 */
export const processHtmlContent = (
  html: string,
  options: ProcessHtmlOptions = {},
): ReactNode => {
  const {
    navigate,
    className,
    ParagraphComponent,
    splitSpansIntoParagraphs = false,
  } = options;

  if (!html) {
    return null;
  }

  // If splitting spans into paragraphs and no links present, use simpler logic
  if (splitSpansIntoParagraphs && !html.includes("<a ")) {
    const spanRegex = /<span[^>]*>(.*?)<\/span>/g;
    const paragraphs = [];
    let match;

    while ((match = spanRegex.exec(html)) !== null) {
      paragraphs.push(match[1]);
    }

    // If no spans found, treat the whole content as one paragraph
    if (paragraphs.length === 0) {
      if (ParagraphComponent) {
        return createElement(
          ParagraphComponent,
          { variant: "paragraph" },
          createElement("span", {
            dangerouslySetInnerHTML: { __html: html },
          }),
        );
      }
      return createElement("span", {
        className,
        dangerouslySetInnerHTML: { __html: html },
      });
    }

    // Multiple spans found - render as separate paragraphs
    return createElement(
      "div",
      { className: className || "flex flex-col gap-4" },
      paragraphs.map((paragraph, index) =>
        ParagraphComponent
          ? createElement(
              ParagraphComponent,
              { key: index, variant: "paragraph" },
              createElement("span", {
                dangerouslySetInnerHTML: { __html: paragraph },
              }),
            )
          : createElement("span", {
              key: index,
              dangerouslySetInnerHTML: { __html: paragraph },
            }),
      ),
    );
  }

  // Original link processing logic
  if (!navigate || !html.includes("<a ")) {
    return createElement("span", {
      className,
      dangerouslySetInnerHTML: { __html: html },
    });
  }

  const parts: ReactNode[] = [];
  const regexPattern = /<a\s+(?:[^>]*?\s+)?href="([^"]*)"[^>]*>(.*?)<\/a>/i;
  let remainingHtml = html;
  let key = 0;

  let match = regexPattern.exec(remainingHtml);
  while (match !== null) {
    const matchIndex = match.index;

    // Add text before the link
    if (matchIndex > 0) {
      parts.push(
        createElement("span", {
          key: `text-${key}`,
          dangerouslySetInnerHTML: {
            __html: remainingHtml.substring(0, matchIndex),
          },
        }),
      );
      key += 1;
    }

    let href = match[1];

    // Transform /collections/technique-name to /members/techniques/technique-name
    if (href.startsWith("/collections/")) {
      const techniqueName = href.substring("/collections/".length);
      href = `/techniques/${techniqueName}`;
    }

    href = href.toLowerCase();

    // Extract text from potential HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = match[2];
    const linkText = tempDiv.textContent || tempDiv.innerText || match[2];

    parts.push(
      createElement(
        "a",
        {
          key: `link-${key}`,
          href: href,
          onClick: (e: MouseEvent) => {
            if (e.ctrlKey || e.metaKey || e.shiftKey) {
              return;
            }
            e.preventDefault();
            console.log("Navigating to ", href);
            navigate(href.replace("/members", ""));
          },
          className: "text-cta hover:text-cta-hover underline",
        },
        linkText,
      ),
    );
    key += 1;

    remainingHtml = remainingHtml.substring(matchIndex + match[0].length);
    match = regexPattern.exec(remainingHtml);
  }

  // Add any remaining text
  if (remainingHtml.length > 0) {
    parts.push(
      createElement("span", {
        key: `text-${key}`,
        dangerouslySetInnerHTML: { __html: remainingHtml },
      }),
    );
  }

  return createElement(Fragment, null, ...parts);
};

export function hexToRgbA(hex: string, alpha: number): string {
  if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    throw new Error(`Bad Hex: ${hex}`);
  }
  const c = hex.substring(1).split("");
  let d: number;
  if (c.length === 3) {
    d = parseInt("0x" + [c[0], c[0], c[1], c[1], c[2], c[2]].join(""), 16);
  } else {
    d = parseInt("0x" + c.join(""), 16);
  }
  return (
    "rgba(" +
    [(d >> 16) & 255, (d >> 8) & 255, d & 255].join(",") +
    `,${alpha})`
  );
}

// Country-specific locales used for price formatting so currency symbol,
// grouping separator, and symbol position match what a user in that country
// would expect (e.g. "525 kr." for DK-DKK vs the en-US default of "DKK 525").
const COUNTRY_LOCALES: Record<string, string> = {
  AR: "es-AR",
  AT: "de-AT",
  AU: "en-AU",
  BE: "nl-BE",
  BR: "pt-BR",
  CA: "en-CA",
  CH: "de-CH",
  CL: "es-CL",
  CN: "zh-CN",
  CY: "el-CY",
  CZ: "cs-CZ",
  DE: "de-DE",
  DK: "da-DK",
  EC: "es-EC",
  EE: "et-EE",
  ES: "es-ES",
  FI: "fi-FI",
  FR: "fr-FR",
  GB: "en-GB",
  GR: "el-GR",
  HK: "zh-HK",
  HU: "hu-HU",
  IE: "en-IE",
  IL: "he-IL",
  IN: "en-IN",
  IT: "it-IT",
  JM: "en-JM",
  JP: "ja-JP",
  KR: "ko-KR",
  LT: "lt-LT",
  LU: "fr-LU",
  LV: "lv-LV",
  ML: "fr-ML",
  MX: "es-MX",
  NL: "nl-NL",
  NO: "nb-NO",
  NZ: "en-NZ",
  PR: "es-PR",
  PT: "pt-PT",
  SE: "sv-SE",
  SG: "en-SG",
  SI: "sl-SI",
  SK: "sk-SK",
  TW: "zh-TW",
  US: "en-US",
  ZA: "en-ZA",
};

export const formatPrice = ({
  amount,
  currency,
  countryCode,
}: {
  amount: number;
  currency: string;
  countryCode?: string;
}) => {
  const locale = (countryCode && COUNTRY_LOCALES[countryCode]) || "default";
  const formatter = new Intl.NumberFormat(
    locale,
    currencyFormatOptions(currency),
  );
  return formatter.format(amount);
};

/**
 * Whole-unit currency formatting. Both fraction bounds are set on purpose:
 * with only `maximumFractionDigits: 0`, the minimum resolves to the currency's
 * own default (2 for USD/EUR/...), and a maximum below the minimum is a
 * `RangeError: maximumFractionDigits is out of range` on engines that apply
 * the pre-2023 ECMA-402 ordering (older Safari). That crash took the gift page
 * down on render (Sentry NMH-REACT-D9M, OMG-2592).
 */
export const currencyFormatOptions = (
  currency: string,
): Intl.NumberFormatOptions => ({
  style: "currency",
  currency,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Parses statistic HTML to extract number/percentage and text
 * Preserves HTML entities and formatting as-is for browser rendering
 *
 * @param html - The HTML string containing statistic spans
 * @returns Object with number (or percentage) and text fields
 */
export const parseStatistic = (
  html?: string,
): { number: string; text: string } => {
  if (!html) return { number: "", text: "" };

  // Extract everything between number span tags (preserves HTML entities)
  const numberMatch = html.match(/<span class="number">([^<]+)<\/span>/);

  // Extract text - use greedy match to capture all nested content
  const textMatch = html.match(/<span class="text">(.*)<\/span>/s);

  const number = numberMatch?.[1] || "";
  const text = textMatch?.[1] || "";

  return { number, text };
};
